import React from 'react';
import { Plus, Trash2, ChevronDown, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { cn, GenericTableEditor, SWOTEditor } from './SharedComponents';

interface EditorProps {
    data: any;
    onChange: (data: any) => void;
    readOnly?: boolean;
    isAdmin?: boolean;
    canEditRows?: boolean;
    title?: string;
    onTitleChange?: (val: string) => void;
    resolveColumns?: (defaultCols: any[]) => any[];
    handleColumnsChange?: (newCols: any[]) => void;
}

/**
 * SECCIÓN 7: CARTERA DE PROYECTOS ESTRATÉGICOS
 * Layout de tarjetas premium.
 */
export const ProyectosCardsEditor = ({ 
    data, 
    onChange, 
    readOnly, 
    isAdmin,
    canEditRows,
    title,
    onTitleChange 
}: EditorProps) => {
    const items = Array.isArray(data) ? data : [];
    const effectiveReadOnly = readOnly && !isAdmin;
    const canAddRemove = canEditRows !== undefined ? canEditRows : !effectiveReadOnly;

    const updateProject = (idx: number, field: string, value: any) => {
        if (effectiveReadOnly) return;
        const newItems = [...items];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onChange(newItems);
    };

    const addProject = () => {
        if (!canAddRemove) return;
        const newItems = [...items, { 
            titulo_proyecto: '', 
            descripcion_estrategica: '', 
            avance_implementacion: '', 
            beneficios_socios_directos: '', 
            beneficios_centros_empresariales: '', 
            inversion_presupuesto: '', 
            aliados_estrategicos: '', 
            descripcion_avances: '' 
        }];
        onChange(newItems);
    };

    const removeProject = (idx: number) => {
        if (!canAddRemove) return;
        if (!confirm('¿Estás seguro de que deseas eliminar este proyecto?')) return;
        const newItems = items.filter((_, i) => i !== idx);
        onChange(newItems);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {canAddRemove && (
                <div className="flex justify-end mb-5 px-4">
                    <button 
                        onClick={addProject}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-3 shadow-lg shadow-blue-500/20 min-h-[44px]"
                    >
                        <Plus size={16} /> AGREGAR PROYECTO
                    </button>
                </div>
            )}

            
            <div className="grid grid-cols-1 gap-5">
                {items.length === 0 && (
                    <div className="col-span-full p-10 bg-slate-900/50 border border-dashed border-white/10 rounded-xl text-center">
                        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">No hay proyectos registrados</p>
                    </div>
                )}
                {items.map((project: any, idx: number) => (
                    <div key={idx} className="bg-slate-900 border border-white/5 p-5 md:p-6 rounded-3xl relative overflow-hidden group hover:bg-blue-500/5 transition-all">
                        <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                            {canAddRemove && (
                                <button 
                                    onClick={() => removeProject(idx)}
                                    className="w-12 h-12 bg-rose-500/10 text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all min-h-[44px] min-w-[44px]"
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                        </div>
                        <div className="space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
                                {/* 1. Título */}
                                <div className="md:col-span-2 space-y-1">
                                    <label className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Título de proyecto</label>
                                    <input 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-transparent text-white font-black text-xl uppercase tracking-tight focus:outline-none focus:text-blue-400 border-b border-white/5 focus:border-blue-500/50 pb-1 min-h-[44px]"
                                        value={project.titulo_proyecto || project.nombre || ''}
                                        onChange={e => updateProject(idx, 'titulo_proyecto', e.target.value)}
                                        placeholder="NOMBRE DEL PROYECTO..."
                                    />
                                </div>
                                {/* Estatus */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Estatus</label>
                                    <select 
                                        disabled={effectiveReadOnly}
                                        className="w-full bg-slate-950 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-blue-500/30 min-h-[44px]"
                                        value={project.estatus || ''}
                                        onChange={e => updateProject(idx, 'estatus', e.target.value)}
                                    >
                                         <option value="">-- SELECCIONAR --</option>
                                         <option value="EN PROCESO">EN PROCESO</option>
                                         <option value="COMPLETADO">COMPLETADO</option>
                                         <option value="PAUSADO">PAUSADO</option>
                                         <option value="PENDIENTE">PENDIENTE</option>
                                     </select>
                                </div>
                            </div>
                            {/* 2. Descripción estratégica */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Descripción estratégica</label>
                                <textarea 
                                    readOnly={effectiveReadOnly}
                                    className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-blue-500/30 resize-none min-h-[64px]"
                                    rows={2}
                                    value={project.descripcion_estrategica || project.objetivo || ''}
                                    onChange={e => updateProject(idx, 'descripcion_estrategica', e.target.value)}
                                    placeholder="OBJETIVO Y PROPÓSITO DEL PROYECTO..."
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* 3. Beneficios para Socios Directos */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Beneficios para Socios Directos</label>
                                    <textarea 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-indigo-500/30 resize-none min-h-[64px]"
                                        rows={2}
                                        value={project.beneficios_socios_directos || ''}
                                        onChange={e => updateProject(idx, 'beneficios_socios_directos', e.target.value)}
                                    />
                                </div>
                                {/* 4. Beneficios para Centros Empresariales */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Beneficios para Centros Empresariales</label>
                                    <textarea 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-purple-500/30 resize-none min-h-[64px]"
                                        rows={2}
                                        value={project.beneficios_centros_empresariales || ''}
                                        onChange={e => updateProject(idx, 'beneficios_centros_empresariales', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* 5. Inversión/Presupuesto */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Inversión/Presupuesto</label>
                                    <input 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500/30 min-h-[44px]"
                                        value={project.inversion_presupuesto || ''}
                                        onChange={e => updateProject(idx, 'inversion_presupuesto', e.target.value)}
                                    />
                                </div>
                                {/* 6. Aliados Estratégicos */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-cyan-500 uppercase tracking-widest">Aliados Estratégicos</label>
                                    <input 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-cyan-500/30 min-h-[44px]"
                                        value={project.aliados_estrategicos || ''}
                                        onChange={e => updateProject(idx, 'aliados_estrategicos', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* 7. Avance de implementación con Barra de % */}
                            <div className="space-y-3 bg-slate-950/30 p-4 md:p-5 rounded-2xl border border-white/5">
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Avance de implementación</label>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl font-black text-white italic">{project.progreso_porcentaje || 0}%</span>
                                        {!effectiveReadOnly && (
                                            <input 
                                                type="range"
                                                min="0"
                                                max="100"
                                                step="5"
                                                className="w-32 accent-emerald-500 h-1.5 bg-slate-800 rounded-lg cursor-pointer"
                                                value={project.progreso_porcentaje || 0}
                                                onChange={e => updateProject(idx, 'progreso_porcentaje', parseInt(e.target.value))}
                                            />
                                        )}
                                    </div>
                                </div>
                                
                                {/* Barra Visual */}
                                <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden border border-white/5">
                                    <div 
                                        className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-500"
                                        style={{ width: `${project.progreso_porcentaje || 0}%` }}
                                    ></div>
                                </div>

                                <textarea 
                                    readOnly={effectiveReadOnly}
                                    className="w-full bg-transparent border border-white/5 rounded-2xl p-3 text-xs text-white/60 focus:outline-none focus:border-emerald-500/30 resize-none min-h-[60px]"
                                    rows={2}
                                    value={project.avance_implementacion || ''}
                                    onChange={e => updateProject(idx, 'avance_implementacion', e.target.value)}
                                    placeholder="DETALLES TÉCNICOS DEL AVANCE..."
                                />
                            </div>

                            {/* 8. Descripción de avances */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Descripción de avances / Evidencia</label>
                                <input 
                                    readOnly={effectiveReadOnly}
                                    className="w-full bg-slate-950/50 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-blue-500/30 min-h-[44px]"
                                    value={project.descripcion_avances || project.evidencia || ''}
                                    onChange={e => updateProject(idx, 'descripcion_avances', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * SECCIÓN 8: CONVENIOS Y ALIANZAS ESTRATÉGICAS
 * Layout de tarjetas premium.
 */
export const ConveniosCardsEditor = ({ 
    data, 
    onChange, 
    readOnly, 
    isAdmin,
    canEditRows,
    title,
    onTitleChange 
}: EditorProps) => {
    const items = Array.isArray(data) ? data : [];
    const effectiveReadOnly = readOnly && !isAdmin;
    const canAddRemove = canEditRows !== undefined ? canEditRows : !effectiveReadOnly;

    const updateItem = (idx: number, field: string, value: any) => {
        if (effectiveReadOnly) return;
        const newItems = [...items];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onChange(newItems);
    };

    const addItem = () => {
        if (!canAddRemove) return;
        const newItem = { 
            aliado: '', 
            objetivo: '', 
            vencimiento: '', 
            estatus: 'VIGENTE', 
            resultado: '', 
            border: '', // keeping keys consistent
            beneficio: '' 
        };
        onChange([...items, newItem]);
    };

    const removeItem = (idx: number) => {
        if (!canAddRemove) return;
        if (!confirm('¿Estás seguro de que deseas eliminar este convenio?')) return;
        const newItems = items.filter((_, i) => i !== idx);
        onChange(newItems);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {canAddRemove && (
                <div className="flex justify-end mb-5 px-4">
                    <button 
                        onClick={addItem}
                        className="bg-amber-600 hover:bg-amber-500 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-3 shadow-lg shadow-amber-500/20 min-h-[44px]"
                    >
                        <Plus size={16} /> AGREGAR CONVENIO
                    </button>
                </div>
            )}

            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {items.length === 0 && (
                    <div className="col-span-full p-10 bg-slate-900/50 border border-dashed border-white/10 rounded-xl text-center">
                        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">No hay convenios registrados</p>
                    </div>
                )}
                {items.map((item: any, idx: number) => (
                    <div key={idx} className="bg-slate-900 border border-white/5 p-5 rounded-2xl relative overflow-hidden group hover:bg-blue-500/5 transition-all">
                        <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                            {canAddRemove && (
                                <button 
                                    onClick={() => removeItem(idx)}
                                    className="w-12 h-12 bg-rose-500/10 text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all min-h-[44px] min-w-[44px]"
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                        </div>
                        <div className="space-y-4.5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4.5">
                                {/* Aliado */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Aliado / Institución</label>
                                    <input 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-transparent text-white font-black text-xl uppercase tracking-tight focus:outline-none focus:text-amber-400 border-b border-white/5 focus:border-amber-500/50 pb-1 min-h-[44px]"
                                        value={item.aliado || item.institucion || item.aliado_institucion || ''}
                                        onChange={e => updateItem(idx, 'aliado', e.target.value)}
                                        placeholder="NOMBRE DE LA INSTITUCIÓN..."
                                    />
                                </div>
                                {/* Estatus y Vencimiento */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Estatus</label>
                                        <select 
                                            disabled={effectiveReadOnly}
                                            className="w-full bg-slate-950 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500/30 min-h-[44px]"
                                            value={item.estatus || ''}
                                            onChange={e => updateItem(idx, 'estatus', e.target.value)}
                                        >
                                            <option value="VIGENTE">VIGENTE</option>
                                            <option value="POR VENCER">POR VENCER</option>
                                            <option value="RENOVACIÓN">RENOVACIÓN</option>
                                            <option value="PROCESO">PROCESO</option>
                                            <option value="CONCLUIDO">CONCLUIDO</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Vencimiento</label>
                                        <input 
                                            readOnly={effectiveReadOnly}
                                            className="w-full bg-slate-950 border border-white/5 rounded-2xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-amber-500/30 min-h-[44px]"
                                            value={item.vencimiento || ''}
                                            onChange={e => updateItem(idx, 'vencimiento', e.target.value)}
                                            placeholder="FECHA O PERIODO"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Objetivo */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Objetivo del Convenio</label>
                                <textarea 
                                    readOnly={effectiveReadOnly}
                                    className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-amber-500/30 resize-none min-h-[50px]"
                                    rows={2}
                                    value={item.objetivo || item.convenio || ''}
                                    onChange={e => updateItem(idx, 'objetivo', e.target.value)}
                                    placeholder="ALCANCE Y PROPÓSITO DE LA ALIANZA..."
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4.5">
                                {/* Resultados */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Resultados Obtenidos</label>
                                    <textarea 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-emerald-500/30 resize-none min-h-[64px]"
                                        rows={3}
                                        value={item.resultado || ''}
                                        onChange={e => updateItem(idx, 'resultado', e.target.value)}
                                        placeholder="LOGROS Y ENTREGABLES..."
                                    />
                                </div>
                                {/* Beneficios */}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Beneficios para Socios</label>
                                    <textarea 
                                        readOnly={effectiveReadOnly}
                                        className="w-full bg-slate-950/50 border border-white/5 rounded-2xl p-3 text-xs text-white/80 focus:outline-none focus:border-blue-500/30 resize-none min-h-[64px]"
                                        rows={3}
                                        value={item.benefit || item.beneficio || ''}
                                        onChange={e => updateItem(idx, 'beneficio', e.target.value)}
                                        placeholder="VALOR AGREGADO PARA LA MEMBRESÍA..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * SECCIÓN 3: DIAGNÓSTICO UNIFICADO
 * Incluye Temas, SWOT e Intervenciones en un solo objeto.
 */
export const DiagnosticoFormat = ({ 
    data, 
    onChange, 
    readOnly, 
    isAdmin,
    canEditRows,
    resolveColumns,
    handleColumnsChange 
}: EditorProps) => {
    const diag = data || {};
    const effectiveReadOnly = readOnly && !isAdmin;

    const updatePart = (key: string, val: any) => {
        onChange({ ...diag, [key]: val });
    };

    return (
        <div className="space-y-4 animate-in fade-in duration-500">
            {/* Diagnóstico Sectorial */}
            <div className="bg-slate-900/50 border border-white/5 p-3 sm:p-4 rounded-xl shadow-2xl relative overflow-hidden">
                <div className="space-y-1 mb-3">
                    {(isAdmin && !readOnly) ? (
                        <div className="space-y-2">
                            <input 
                                className="bg-transparent text-white font-black text-lg sm:text-xl uppercase tracking-tighter italic border-b border-dashed border-slate-700 focus:border-blue-500 focus:outline-none w-full"
                                value={diag.header1_title || "1. DIAGNÓSTICO DEL SECTOR"}
                                onChange={e => updatePart('header1_title', e.target.value)}
                            />
                            <input 
                                className="bg-transparent text-blue-400 text-[9px] font-black uppercase tracking-[0.3em] border-b border-dashed border-slate-700 focus:border-blue-500 focus:outline-none w-full"
                                value={diag.header1_subtitle || "Coyuntura y Entorno Externo"}
                                onChange={e => updatePart('header1_subtitle', e.target.value)}
                            />
                        </div>
                    ) : (
                        <>
                            <h3 className="text-white font-black text-lg sm:text-xl uppercase tracking-tighter italic">{diag.header1_title || "1. DIAGNÓSTICO DEL SECTOR"}</h3>
                            <p className="text-blue-400 text-[9px] font-black uppercase tracking-[0.3em]">{diag.header1_subtitle || "Coyuntura y Entorno Externo"}</p>
                        </>
                    )}
                </div>
                <GenericTableEditor
                    readOnly={effectiveReadOnly}
                    isAdmin={isAdmin} canEditRows={canEditRows}
                    columns={resolveColumns ? resolveColumns([
                        { key: 'tema', label: 'Tema / Tendencia', className: 'font-bold' },
                        { key: 'descripcion', label: 'Descripción / Situación Actual' },
                        { key: 'riesgo', label: 'Riesgo (B/M/A)', className: 'w-32 text-center' }
                    ]) : []}
                    data={diag.temas_sector || []}
                    onChange={val => updatePart('temas_sector', val)}
                    onColumnsChange={handleColumnsChange}
                    onTitleChange={val => updatePart('temas_title', val)}
                    title={diag.temas_title || "TEMAS Y TENDENCIAS"}
                />
                <div className="mt-5 border-t border-white/5 pt-5">
                    {(isAdmin && !readOnly) ? (
                        <input 
                            className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-3 block bg-transparent border-b border-dashed border-slate-700 focus:border-blue-500 focus:outline-none w-full"
                            value={diag.swot_title || "ANÁLISIS FODA SECTORIAL"}
                            onChange={e => updatePart('swot_title', e.target.value)}
                        />
                    ) : (
                        <label className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-3 block">{diag.swot_title || "ANÁLISIS FODA SECTORIAL"}</label>
                    )}
                    <SWOTEditor
                        readOnly={effectiveReadOnly}
                        isAdmin={isAdmin}
                        data={diag.swot_sector || { threats: '', strengths: '', weaknesses: '', opportunities: '' }}
                        onChange={val => updatePart('swot_sector', val)}
                    />
                </div>
            </div>

            {/* Intervenciones Previas */}
            {(!diag._config_intervenciones_hidden && !diag._config_intervenciones_disabled || isAdmin) && (
                <div className="bg-slate-900/40 border border-white/5 p-3 sm:p-4 rounded-xl shadow-xl">
                    <div className="space-y-1 mb-3">
                        {(isAdmin && !readOnly) ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input 
                                        className="bg-transparent text-white font-black text-lg sm:text-xl uppercase tracking-tighter italic border-b border-dashed border-slate-700 focus:border-amber-500 focus:outline-none w-full"
                                        value={diag.header2_title || "2. INTERVENCIONES Y LOGROS PREVIOS"}
                                        onChange={e => updatePart('header2_title', e.target.value)}
                                    />
                                    <button
                                        onClick={() => updatePart('_config_intervenciones_hidden', !diag._config_intervenciones_hidden)}
                                        className="text-white/40 hover:text-amber-400 p-1.5 hover:bg-slate-800 rounded transition-all flex items-center justify-center min-w-[36px] min-h-[36px]"
                                        title={diag._config_intervenciones_hidden ? "Mostrar Subsección" : "Ocultar Subsección"}
                                    >
                                        {diag._config_intervenciones_hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                    <button
                                        onClick={() => {
                                            const nextDisabled = !diag._config_intervenciones_disabled;
                                            onChange({
                                                ...diag,
                                                _config_intervenciones_disabled: nextDisabled,
                                                _config_intervenciones_hidden: nextDisabled ? true : diag._config_intervenciones_hidden
                                            });
                                        }}
                                        className="text-white/40 hover:text-red-400 p-1.5 hover:bg-slate-800 rounded transition-all flex items-center justify-center min-w-[36px] min-h-[36px]"
                                        title={diag._config_intervenciones_disabled ? "Activar Subsección" : "Desactivar Subsección"}
                                    >
                                        <AlertTriangle size={16} className={diag._config_intervenciones_disabled ? "text-red-500" : ""} />
                                    </button>
                                </div>
                                <input 
                                    className="bg-transparent text-amber-400 text-[9px] font-black uppercase tracking-[0.3em] border-b border-dashed border-slate-700 focus:border-amber-500 focus:outline-none w-full"
                                    value={diag.header2_subtitle || "Antecedentes Históricos"}
                                    onChange={e => updatePart('header2_subtitle', e.target.value)}
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <h3 className="text-white font-black text-lg sm:text-xl uppercase tracking-tighter italic">{diag.header2_title || "2. INTERVENCIONES Y LOGROS PREVIOS"}</h3>
                                <div className="flex gap-1.5">
                                    {diag._config_intervenciones_hidden && <span className="px-1.5 py-0.5 bg-slate-800 text-slate-400 border border-slate-700 text-[8px] font-black rounded uppercase tracking-widest leading-none">Oculta</span>}
                                    {diag._config_intervenciones_disabled && <span className="px-1.5 py-0.5 bg-red-950/40 text-red-400 border border-red-900/40 text-[8px] font-black rounded uppercase tracking-widest leading-none">Desactivada</span>}
                                </div>
                                {!diag._config_intervenciones_hidden && !diag._config_intervenciones_disabled && diag.header2_subtitle && (
                                    <p className="text-amber-400 text-[9px] font-black uppercase tracking-[0.3em]">{diag.header2_subtitle}</p>
                                )}
                            </div>
                        )}
                    </div>
                    <GenericTableEditor
                        readOnly={effectiveReadOnly}
                        isAdmin={isAdmin} canEditRows={canEditRows}
                        columns={resolveColumns ? resolveColumns([
                            { key: 'tema', label: 'Iniciativa / Logro' },
                            { key: 'descripcion', label: 'Resultado / Impacto' },
                            { key: 'periodo', label: 'Periodo', className: 'w-32 text-center' }
                        ]) : []}
                        data={diag.intervenciones_previas || []}
                        onChange={val => updatePart('intervenciones_previas', val)}
                        onColumnsChange={handleColumnsChange}
                        onTitleChange={val => updatePart('intervenciones_title', val)}
                        title={diag.intervenciones_title || "HISTÓRICO DE INTERVENCIONES"}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * SECCIÓN 5: COMISIONES LOCALES
 * Gestor de vinculación con centros.
 */
export const ComisionesManager = ({ 
    data, 
    onChange, 
    readOnly, 
    isAdmin,
    canEditRows,
    resolveColumns,
    handleColumnsChange 
}: EditorProps) => {
    const effectiveReadOnly = readOnly && !isAdmin;

    const sortedData = React.useMemo(() => {
        const arr = Array.isArray(data) ? [...data] : [];
        const mapped = arr.map((item, idx) => ({ item, idx }));
        mapped.sort((a, b) => {
            const valA = String(a.item?.centro || '').trim().toLowerCase();
            const valB = String(b.item?.centro || '').trim().toLowerCase();
            return valA.localeCompare(valB, 'es', { sensitivity: 'base' });
        });
        return mapped;
    }, [data]);

    const handleDataChange = (newSortedItems: any[]) => {
        const originalArray = Array.isArray(data) ? [...data] : [];
        newSortedItems.forEach((newItem, sortedIdx) => {
            const originalIdx = sortedData[sortedIdx]?.idx;
            if (originalIdx !== undefined && originalIdx < originalArray.length) {
                originalArray[originalIdx] = newItem;
            } else {
                originalArray.push(newItem);
            }
        });
        if (newSortedItems.length < sortedData.length) {
            const activeOriginalIndices = new Set(
                newSortedItems.map((_, sortedIdx) => sortedData[sortedIdx]?.idx).filter(idx => idx !== undefined)
            );
            const filteredOriginal = originalArray.filter((_, idx) => activeOriginalIndices.has(idx));
            onChange(filteredOriginal);
        } else {
            onChange(originalArray);
        }
    };

    return (
        <div className="space-y-6 animate-in slide-in-from-bottom duration-700">
            <div className="bg-purple-600/10 border border-purple-500/20 p-4 sm:p-5 rounded-2xl relative overflow-hidden shadow-2xl">
                <div className="space-y-1 mb-4">
                    <h3 className="text-white font-black text-lg sm:text-xl uppercase tracking-tighter italic">COMISIONES LOCALES</h3>
                    <p className="text-purple-400 text-[9px] font-black uppercase tracking-[0.3em]">Vertebración Nacional (S5)</p>
                </div>
                <GenericTableEditor
                    readOnly={effectiveReadOnly}
                    isAdmin={isAdmin} canEditRows={canEditRows}
                    columns={resolveColumns ? resolveColumns([
                        { key: 'centro', label: 'Centro Empresarial' },
                        { key: 'instancia', label: 'Estatus', type: 'select', options: ['Existe', 'No Existe'] },
                        { key: 'meta_vinculacion', label: 'Meta 2026', className: 'bg-indigo-500/10 text-indigo-400 font-black', type: 'select', options: ['Mantener', 'Activar', 'Crear', 'NO APLICA'] },
                        { key: 'comentarios_vinculacion', label: 'Comentarios / Acciones' }
                    ]) : []}
                    data={sortedData.map(d => d.item)}
                    onChange={handleDataChange}
                    onColumnsChange={handleColumnsChange}
                    title="ESTADO DE VINCULACIÓN"
                />
            </div>
        </div>
    );
};
